import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import * as constants from '../../constants/CONSTANT';
import { crmAuthHeaders } from '../../api/DataApi';

/**
 * When a record view is chosen, omit editLayoutId so the API resolves layout via
 * object_layout_role for that recordview_id.
 */
export function resolveEmitPayload(rvId, layoutFallbackWhenNoRv) {
  if (rvId && String(rvId).trim() !== '') {
    return {
      recordviewId: String(rvId).trim(),
      editLayoutId: '',
    };
  }
  const editLayoutId = layoutFallbackWhenNoRv
    ? String(layoutFallbackWhenNoRv).trim()
    : '';
  return { recordviewId: '', editLayoutId };
}

/**
 * Role-assigned record view ids for an object from permission_config.recordviewsByObject.
 * Returns null when the role has no restriction (no row or empty recordview_ids) — callers show all active views.
 * Returns string[] when the role has at least one assigned id — callers must filter to this set.
 */
export async function fetchRoleRecordviewIdAllowlistForObject(objectName) {
  if (!objectName || !String(objectName).trim()) {
    return null;
  }
  const headers = { ...crmAuthHeaders() };
  const token = localStorage.getItem('token');
  let roleId = '';
  if (token) {
    try {
      const d = jwt_decode(token);
      roleId = d?.role_id || d?.roleId || '';
    } catch {
      /* ignore */
    }
  }
  if (!roleId) {
    return null;
  }
  try {
    const cfgRes = await axios.get(
      `${constants.API_BASE_URL}/api/permissions/role/${roleId}/recordview-config`,
      { headers }
    );
    if (!cfgRes.data?.success || !Array.isArray(cfgRes.data.data)) {
      return null;
    }
    const cfgRow = cfgRes.data.data.find(
      (row) =>
        String(row.object_name || '').toLowerCase().trim() ===
        String(objectName).toLowerCase().trim()
    );
    const ids = Array.isArray(cfgRow?.recordview_ids)
      ? cfgRow.recordview_ids.map((x) => String(x)).filter((s) => s.trim() !== '')
      : [];
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}

/**
 * Loads active record views allowed for the current role (same rules as the picker UI).
 * @returns {{ allowed: Array, fallbackLayout: string }}
 */
export async function fetchRecordViewChoicesForNewRecord(objectName) {
  if (!objectName) {
    return { allowed: [], fallbackLayout: '' };
  }
  const headers = { ...crmAuthHeaders() };
  const token = localStorage.getItem('token');
  let roleId = '';
  if (token) {
    try {
      const d = jwt_decode(token);
      roleId = d?.role_id || d?.roleId || '';
    } catch {
      /* ignore */
    }
  }

  const [rvRes, cfgRes] = await Promise.all([
    axios.get(
      `${constants.API_BASE_URL}/api/recordview/object/${encodeURIComponent(objectName)}`,
      { headers }
    ),
    roleId
      ? axios.get(
          `${constants.API_BASE_URL}/api/permissions/role/${roleId}/recordview-config`,
          { headers }
        )
      : Promise.resolve({ data: { success: false } }),
  ]);

  const allRows = rvRes.data?.success ? rvRes.data.data || [] : [];
  const active = allRows.filter((r) => !r.is_deleted && r.is_active !== false);

  let cfgRow = null;
  if (cfgRes.data?.success && Array.isArray(cfgRes.data.data)) {
    cfgRow = cfgRes.data.data.find(
      (row) =>
        String(row.object_name || '').toLowerCase().trim() ===
        String(objectName).toLowerCase().trim()
    );
  }
  const configuredIds = (cfgRow?.recordview_ids || [])
    .map((x) => String(x))
    .filter((s) => s.trim() !== '');
  const allowedIds = new Set(configuredIds);
  let allowed = active;
  if (allowedIds.size > 0) {
    allowed = active.filter((r) => allowedIds.has(String(r.id)));
  }

  const fallbackLayout = '';

  return { allowed, fallbackLayout };
}

/**
 * Record view step for new-record flows. If the object has at least one allowed record view,
 * parent should confirm via ref.confirm() (e.g. footer Next). When no record views apply,
 * layout resolution falls back to object_layout_role / server defaults.
 *
 * @param {string} [initialRecordviewId] - Pre-select when it matches an allowed view (e.g. URL/query).
 * @param {'inline'|'none'} [confirmPlacement] - If 'none', do not render the inline Next button (use modal footer).
 * @param {Array} [allowedChoices] - When set (multi-choice list from a parent prefetch), skip network and show the dropdown only.
 */
const RecordViewNewRecordPicker = forwardRef(function RecordViewNewRecordPicker(
  {
    objectName,
    onResolved,
    disabled,
    initialRecordviewId,
    confirmPlacement = 'inline',
    onPickInteractionChange,
    allowedChoices,
  },
  ref
) {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState('');
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onPickInteractionChangeRef = useRef(onPickInteractionChange);
  onPickInteractionChangeRef.current = onPickInteractionChange;

  useEffect(() => {
    onPickInteractionChangeRef.current?.({
      loading,
      hasChoices: options.length > 0,
      canConfirm: !loading && options.length > 0 && !!(value || (options[0] && options[0].id)),
    });
  }, [loading, options.length, value]);

  useImperativeHandle(ref, () => ({
    confirm: () => {
      const v = value || (options[0] && String(options[0].id)) || '';
      if (!v || options.length === 0) return false;
      onResolvedRef.current(resolveEmitPayload(v, ''));
      return true;
    },
    isLoading: () => loading,
    hasChoices: () => options.length > 0,
    canConfirm: () => !loading && options.length > 0 && !!(value || options[0]),
  }));

  useEffect(() => {
    let cancelled = false;

    if (Array.isArray(allowedChoices) && allowedChoices.length > 1) {
      const first = allowedChoices[0];
      const preferred =
        initialRecordviewId &&
        allowedChoices.some((r) => String(r.id) === String(initialRecordviewId).trim())
          ? String(initialRecordviewId).trim()
          : first
            ? String(first.id)
            : '';
      setLoading(false);
      setOptions(allowedChoices);
      setValue(preferred);
      return undefined;
    }

    async function load() {
      if (!objectName) {
        setLoading(false);
        onResolvedRef.current({ recordviewId: '', editLayoutId: '' });
        return;
      }
      setLoading(true);
      try {
        const { allowed, fallbackLayout } = await fetchRecordViewChoicesForNewRecord(objectName);

        if (allowed.length === 0) {
          if (!cancelled) {
            setOptions([]);
            setValue('');
            onResolvedRef.current({
              recordviewId: '',
              editLayoutId: fallbackLayout,
            });
          }
          return;
        }

        if (allowed.length === 1) {
          const onlyId = String(allowed[0].id);
          if (!cancelled) {
            onResolvedRef.current(resolveEmitPayload(onlyId, ''));
          }
          return;
        }

        const first = allowed[0];
        const preferred =
          initialRecordviewId &&
          allowed.some((r) => String(r.id) === String(initialRecordviewId).trim())
            ? String(initialRecordviewId).trim()
            : first
              ? String(first.id)
              : '';

        if (!cancelled) {
          setOptions(allowed);
          setValue(preferred);
        }
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
          setValue('');
          onResolvedRef.current({ recordviewId: '', editLayoutId: '' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [objectName, initialRecordviewId, allowedChoices]);

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleNext = () => {
    const v = value || (options[0] && String(options[0].id)) || '';
    if (!v) return;
    onResolvedRef.current(resolveEmitPayload(v, ''));
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 py-2 text-muted small">
        <Spinner animation="border" size="sm" />
        Loading record views…
      </div>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="recordview-new-picker">
      <Form.Label className="small fw-semibold text-secondary">Record view</Form.Label>
      <Form.Select
        size="sm"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        aria-label="Record view"
      >
        {options.map((r) => (
          <option key={r.id} value={r.id}>
            {r.view_label || r.name || r.id}
          </option>
        ))}
      </Form.Select>
      {confirmPlacement === 'inline' && (
        <div className="d-flex justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleNext}
            disabled={disabled || !value}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
});

RecordViewNewRecordPicker.displayName = 'RecordViewNewRecordPicker';

export default RecordViewNewRecordPicker;
